import { createContext, useContext, type CSSProperties } from 'react';

export const GALLERY_COLOR_SCHEME_OPTIONS = [
  { id: 'storyly', name: 'Storyly', accentColor: '#2D2D2D', backgroundColor: '#FAF9F7', accentTextColor: '#FFFFFF' },
  { id: 'noir', name: 'Noir', accentColor: '#111111', backgroundColor: '#FFFFFF', accentTextColor: '#FFFFFF' },
  { id: 'karoo', name: 'Karoo', accentColor: '#56351F', backgroundColor: '#F3EEE6', accentTextColor: '#FFFFFF' },
  { id: 'ochre', name: 'Ochre', accentColor: '#5C4B2A', backgroundColor: '#F2E5BE', accentTextColor: '#FFFFFF' },
  { id: 'fynbos', name: 'Fynbos', accentColor: '#34402E', backgroundColor: '#EDF2ED', accentTextColor: '#FFFFFF' },
  { id: 'atlantic', name: 'Atlantic', accentColor: '#334954', backgroundColor: '#EEF2F4', accentTextColor: '#FFFFFF' },
  { id: 'merlot', name: 'Merlot', accentColor: '#6B2F2B', backgroundColor: '#F7ECEB', accentTextColor: '#FFFFFF' },
  { id: 'lavender', name: 'Lavender', accentColor: '#493A49', backgroundColor: '#F1EEF6', accentTextColor: '#FFFFFF' }
] as const;

export type GalleryColorScheme = (typeof GALLERY_COLOR_SCHEME_OPTIONS)[number]['id'];
export type GalleryBackgroundColor = (typeof GALLERY_COLOR_SCHEME_OPTIONS)[number]['backgroundColor'];
export type GalleryAccentColor = (typeof GALLERY_COLOR_SCHEME_OPTIONS)[number]['accentColor'];
export type GalleryAccentTextColor = (typeof GALLERY_COLOR_SCHEME_OPTIONS)[number]['accentTextColor'];
export type GalleryColorSchemeOption = (typeof GALLERY_COLOR_SCHEME_OPTIONS)[number];

export const DEFAULT_GALLERY_COLOR_SCHEME: GalleryColorScheme = 'storyly';

export const GALLERY_STYLE_OPTIONS = [
  { id: 'mosaic', name: 'Mosaic', description: 'Layered cover and flowing masonry.', experimental: false },
  { id: 'editorial', name: 'Editorial', description: 'Offset cover and flowing masonry.', experimental: false },
  { id: 'classic', name: 'Classic', description: 'Framed cover and flowing masonry.', experimental: false },
  { id: 'filmstrip', name: 'Filmstrip', description: 'Split cover and a cinematic story.', experimental: true }
] as const;

export type GalleryStyle = (typeof GALLERY_STYLE_OPTIONS)[number]['id'];
export type GalleryStoryStyle = Extract<GalleryStyle, 'mosaic' | 'filmstrip'>;
export const DEFAULT_GALLERY_STYLE: GalleryStyle = 'mosaic';

export function normalizeGalleryStyle(value: unknown): GalleryStyle {
  return typeof value === 'string' && GALLERY_STYLE_OPTIONS.some((option) => option.id === value)
    ? value as GalleryStyle
    : DEFAULT_GALLERY_STYLE;
}

export function galleryStoryStyleOf(value: unknown): GalleryStoryStyle {
  return normalizeGalleryStyle(value) === 'filmstrip' ? 'filmstrip' : 'mosaic';
}

export function normalizeGalleryColorScheme(value: unknown): GalleryColorScheme {
  return typeof value === 'string' && GALLERY_COLOR_SCHEME_OPTIONS.some((option) => option.id === value)
    ? value as GalleryColorScheme
    : DEFAULT_GALLERY_COLOR_SCHEME;
}

export function galleryColorSchemeOf(value: unknown): GalleryColorSchemeOption {
  const id = normalizeGalleryColorScheme(value);
  return GALLERY_COLOR_SCHEME_OPTIONS.find((option) => option.id === id) ?? GALLERY_COLOR_SCHEME_OPTIONS[0];
}

export function galleryColorSchemeVariables(value: unknown): CSSProperties {
  const scheme = galleryColorSchemeOf(value);
  return {
    '--client-gallery-background': scheme.backgroundColor,
    '--client-gallery-accent': scheme.accentColor,
    '--client-gallery-accent-text': scheme.accentTextColor,
    '--client-gallery-ink': '#2D2D2D'
  } as CSSProperties;
}

export const GalleryThemeContext = createContext<CSSProperties | null>(null);

export function useGalleryThemeVariables(): CSSProperties | null {
  return useContext(GalleryThemeContext);
}
